import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Button } from '@material-ui/core';
import { poslumaDarkBlue, universalStyles } from '../../../../app/theme';
import ExternalLinkModal from '../../../micro/ExternalLinkModal/ExternalLinkModal';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  diagram: {
    width: '80%',
    marginLeft: '10%',
  },
  externalLink: {
    color: poslumaDarkBlue,
    textTransform: 'none',
    textDecoration: 'underline',
    padding: 0,
    fontSize: 18,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  link: {
    color: poslumaDarkBlue,
    textTransform: 'none',
    textDecoration: 'underline',
    padding: 0,
    fontSize: 'inherit',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  superscript: {
    fontSize: 11,
  },
}));

const InterpretationPage02 = (): JSX.Element => {
  const classes = useStyles();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalUrl, setModalUrl] = useState<string>('');

  const handleClickFDAMedWatch = () => {
    setModalUrl('https://www.fda.gov/medwatch');
    setIsModalOpen(true);
  };

  return (
    <>
      <div className={classes.poslumaBox}>
        <div className={classes.whiteBox}>
          <Typography variant="h4">Indication</Typography>
          <Typography paragraph variant="body1">
            POSLUMA
            <sup>®</sup>
            &nbsp;(flotufolastat F 18) injection is indicated for positron emission tomography (PET) of
            prostate-specific membrane antigen (PSMA) positive lesions in men with prostate cancer
          </Typography>
          <ul className={classes.poslumaList}>
            <li>with suspected metastasis who are candidates for initial definitive therapy</li>
            <li>with suspected recurrence based on elevated serum prostate-specific antigen (PSA) level</li>
          </ul>
          <Typography variant="h4">
            <b>Important Safety Information</b>
          </Typography>
          <ul className={classes.poslumaList}>
            <li>
              <Typography>
                Image interpretation errors can occur with POSLUMA PET. A negative image does not rule out the presence
                of prostate cancer and a positive image does not confirm the presence of prostate cancer. The
                performance of POSLUMA for imaging metastatic pelvic lymph nodes in patients prior to initial definitive
                therapy seems to be affected by serum PSA levels and risk grouping. The performance of POSLUMA for
                imaging patients with biochemical evidence of recurrence of prostate cancer seems to be affected by
                serum PSA levels. Flotufolastat F 18 uptake is not specific for prostate cancer and may occur in other
                types of cancer, in non-malignant processes, and in normal tissues. Clinical correlation, which may
                include histopathological evaluation, is recommended.
              </Typography>
            </li>
            <li>
              <Typography>
                Risk of Image Misinterpretation in Patients with Suspected Prostate Cancer Recurrence: The
                interpretation of POSLUMA PET may differ depending on imaging readers, particularly in the
                prostate/prostate bed region. Because of the associated risk of false positive interpretation, consider
                multidisciplinary consultation and histopathological confirmation when clinical decision-making hinges
                on flotufolastat F18 uptake only in the prostate/prostate bed region or only on uptake interpreted as
                borderline.
              </Typography>
            </li>
            <li>
              <Typography>
                POSLUMA use contributes to a patient’s overall long-term cumulative radiation exposure. Long-term
                cumulative radiation exposure is associated with an increased risk for cancer. Advise patients to
                hydrate before and after administration and to void frequently after administration. Ensure safe
                handling to minimize radiation exposure to the patient and health care providers.
              </Typography>
            </li>
            <li>
              <Typography>
                The adverse reactions reported in ≥ 0.4% of patients in clinical studies were diarrhea, blood pressure
                increase and injection site pain.
              </Typography>
            </li>
            <li>
              <Typography>
                Drug Interactions: androgen deprivation therapy (ADT) and other therapies targeting the androgen
                pathway, such as androgen receptor antagonists, may result in changes in uptake of flotufolastat F 18 in
                prostate cancer. The effect of these therapies on performance of POSLUMA PET has not been established.
              </Typography>
            </li>
          </ul>
          <Typography variant="h4">Important Imaging Considerations</Typography>
          <ul className={classes.poslumaList}>
            <li>
              <Typography variant="body1">
                Training for POSLUMA was developed as a resource to help familiarize you with techniques for the safe
                and effective usage of POSLUMA
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Images should only be interpreted by readers trained in the interpretation of PET images with POSLUMA
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                The responsibility for the accurate and timely acquisition and interpretation of images using POSLUMA
                scanning rests with the nuclear medicine physician or radiologist supervising the PET imaging facility
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                POSLUMA training is not intended to substitute for independent medical judgment of the physician(s)
                responsible for the individual patient’s management nor is it a guarantee of any specific clinical
                results
              </Typography>
            </li>
          </ul>
          <Typography paragraph variant="body1">
            To report suspected adverse reactions to POSLUMA, call 1-844-POSLUMA (1-844-767-5862) or contact FDA at
            1-800-FDA-1088 or&nbsp;
            <Button className={classes.link} onClick={handleClickFDAMedWatch}>
              www.fda.gov/medwatch
            </Button>
            .
          </Typography>
          <Typography paragraph variant="body1">
            Full POSLUMA prescribing information is available at&nbsp;
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              className={classes.link}
              href="https://www.posluma.com/prescribing-information.pdf"
              target="_blank"
              rel="noreferrer"
            >
              <Button className={classes.link}>www.posluma.com/prescribing-information.pdf</Button>
            </a>
            .
          </Typography>
          <ExternalLinkModal
            url={modalUrl}
            isOpen={isModalOpen}
            handleModalOpen={(isOpen: boolean) => setIsModalOpen(isOpen)}
          />
        </div>
      </div>
    </>
  );
};

export default InterpretationPage02;
